/**
 * A Player's bid notifications (#238, PL-019.3, mockup v7 States 8-10): outbid, won and lost. They
 * are stored by the server, worded there (title and message), counted on the bell and listed on the
 * Notifications page like any other notification, and pushed live as ReceiveBidNotification, which
 * pops them up on whatever page the player has open (CommonComponent/BidNotice). Web only: the
 * server leaves them out for the mobile app.
 */
import { detailsPath } from './auctionList';

/** The notificationType of each bid notification. */
export const BID_NOTIFICATION_TYPES = {
    OUTBID: 'Outbid',
    WON: 'Won',
    LOST: 'Lost',
};

/** How long a bid notification's pop-up stays on screen; hovering or focusing it holds it there. */
export const BID_NOTICE_MS = 6000;

/** The Bid History page, which opens on its Winner tab from a Won notification. */
export const BID_HISTORY_PATH = '/Player/Bid';

/** The Player's Notifications page, where every other notification on the bell leads. */
export const PLAYER_NOTIFICATIONS_PATH = '/Player/Notification';

/**
 * Whether a notification is one of the bid notifications.
 *
 * @param {{notificationType?: string|null}} notification A notification from the API or the hub.
 * @returns {boolean}
 */
export const isBidNotification = (notification) =>
    Object.values(BID_NOTIFICATION_TYPES).includes(notification?.notificationType);

/**
 * Where a notification on the Player's bell or Notifications page leads (#238 AC19): Outbid and Lost
 * to the auction's details, which select the team in Bidding now; Won to the Bid History's Winner
 * tab; anything else to the Notifications page.
 *
 * @param {{notificationType?: string|null, auctionId?: number|null}} notification
 * @returns {{to: string, state?: object}}
 */
export const playerNotificationLink = (notification) => {
    if (notification?.notificationType === BID_NOTIFICATION_TYPES.WON) {
        return { to: BID_HISTORY_PATH, state: { targetTab: 'Winner' } };
    }
    if (isBidNotification(notification) && notification.auctionId) {
        return { to: detailsPath(notification.auctionId) };
    }
    return { to: PLAYER_NOTIFICATIONS_PATH };
};

/**
 * Where the outbid pop-up's Place Bid goes (#238 AC14): the auction's details, which select the team
 * in Bidding now and fill Custom Bid Amount with its Next Minimum Bid. It places no bid.
 *
 * @param {{auctionId: number}} notification An Outbid notification.
 * @returns {{to: string, state: {bidPrefill: true}}}
 */
export const outbidPlaceBidTarget = (notification) => ({
    to: detailsPath(notification.auctionId),
    state: { bidPrefill: true },
});

/**
 * The one line a notification shows in the bell's menu: a bid notification's title, any other's
 * message.
 *
 * @param {{title?: string|null, message?: string|null}} notification
 * @returns {string}
 */
export const bellItemText = (notification) =>
    (isBidNotification(notification) && notification.title) || notification?.message || '';
